// Minimaler EPUB3-Builder via JSZip.
// Struktur:
//   mimetype (unkomprimiert, application/epub+zip)
//   META-INF/container.xml
//   OEBPS/content.opf      (Package-Descriptor)
//   OEBPS/nav.xhtml        (Navigation für ToC)
//   OEBPS/styles.css       (vereinfachtes Stylesheet)
//   OEBPS/chapter-N.xhtml  (jeweils ein Kapitel pro File)
//   OEBPS/images/...       (Bilder, falls inlined)
import JSZip from "jszip";
export async function buildEpub({ settings, bodyHtml, headings, coverDataUrl }) {
    // Wenn nicht explizit gegeben, aus Settings holen — Standardfall.
    const effectiveCoverDataUrl = coverDataUrl ?? settings.cover.imageDataUrl;
    const zip = new JSZip();
    // ---- mimetype muss erstes File und unkomprimiert sein ---------------
    zip.file("mimetype", "application/epub+zip", { compression: "STORE" });
    // ---- META-INF/container.xml -----------------------------------------
    zip.file("META-INF/container.xml", `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`);
    // ---- Body in Kapitel splitten ---------------------------------------
    const chapters = splitChapters(bodyHtml);
    // ---- Cover als Bild verarbeiten -------------------------------------
    let coverImageHref = null;
    let coverMime = null;
    if (settings.cover.mode === "image" && effectiveCoverDataUrl) {
        const m = effectiveCoverDataUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (m) {
            coverMime = m[1];
            // image/jpeg → jpeg, image/png → png, image/webp → webp
            const ext = (coverMime.split("/")[1] || "jpg").replace("jpeg", "jpg");
            coverImageHref = `images/cover.${ext}`;
            zip.file(`OEBPS/${coverImageHref}`, m[2], { base64: true });
        }
    }
    // ---- Stylesheet (kompakt für EPUB) ----------------------------------
    zip.file("OEBPS/styles.css", buildEpubCss(settings));
    // ---- Kapitel-Files schreiben ----------------------------------------
    const manifestItems = [];
    const spineItems = [];
    if (coverImageHref && coverMime) {
        manifestItems.push(`<item id="cover-image" href="${coverImageHref}" media-type="${coverMime}" properties="cover-image"/>`);
        const coverHtml = wrapXhtml("Cover", `<div style="text-align:center; margin:0; padding:0;"><img src="${coverImageHref}" alt="Cover" style="max-width:100%;"/></div>`, settings.meta.language);
        zip.file("OEBPS/cover.xhtml", coverHtml);
        manifestItems.push(`<item id="cover" href="cover.xhtml" media-type="application/xhtml+xml"/>`);
        spineItems.push(`<itemref idref="cover" linear="yes"/>`);
    }
    // Titelseite
    const titleHtml = wrapXhtml(settings.meta.title, `<section class="title-page">
       <h1 class="book-title">${escapeXml(settings.meta.title)}</h1>
       ${settings.meta.subtitle ? `<p class="book-subtitle">${escapeXml(settings.meta.subtitle)}</p>` : ""}
       <p class="book-author">${escapeXml(settings.meta.author)}</p>
     </section>`, settings.meta.language);
    zip.file("OEBPS/title.xhtml", titleHtml);
    manifestItems.push(`<item id="title" href="title.xhtml" media-type="application/xhtml+xml"/>`);
    spineItems.push(`<itemref idref="title"/>`);
    // Inhalte
    chapters.forEach((chapterHtml, i) => {
        const id = `ch${i + 1}`;
        const href = `${id}.xhtml`;
        zip.file(`OEBPS/${href}`, wrapXhtml(`Kapitel ${i + 1}`, `<section class="chapter">${chapterHtml}</section>`, settings.meta.language));
        manifestItems.push(`<item id="${id}" href="${href}" media-type="application/xhtml+xml"/>`);
        spineItems.push(`<itemref idref="${id}"/>`);
    });
    // ---- Navigation (EPUB3 nav.xhtml) -----------------------------------
    zip.file("OEBPS/nav.xhtml", buildNavXhtml(settings, headings, chapters.length));
    manifestItems.push(`<item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>`);
    // ---- content.opf ----------------------------------------------------
    const uuid = `urn:uuid:${randomUUID()}`;
    zip.file("OEBPS/content.opf", `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="bookid" xml:lang="${settings.meta.language}">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="bookid">${uuid}</dc:identifier>
    <dc:title>${escapeXml(settings.meta.title)}</dc:title>
    <dc:creator>${escapeXml(settings.meta.author)}</dc:creator>
    <dc:language>${settings.meta.language}</dc:language>
    <meta property="dcterms:modified">${new Date().toISOString().replace(/\.\d+Z$/, "Z")}</meta>
    ${coverImageHref ? `<meta name="cover" content="cover-image"/>` : ""}
  </metadata>
  <manifest>
    <item id="css" href="styles.css" media-type="text/css"/>
    ${manifestItems.join("\n    ")}
  </manifest>
  <spine>
    ${spineItems.join("\n    ")}
  </spine>
</package>`);
    return zip.generateAsync({ type: "blob", mimeType: "application/epub+zip" });
}
// ---------- Hilfsfunktionen --------------------------------------------------
function splitChapters(bodyHtml) {
    // Splitten an <h1>
    const parts = bodyHtml.split(/(?=<h1[\s>])/).filter((p) => p.trim());
    if (parts.length === 0)
        return [bodyHtml];
    return parts;
}
function wrapXhtml(title, body, lang) {
    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xml:lang="${lang}" lang="${lang}">
<head>
<meta charset="utf-8"/>
<title>${escapeXml(title)}</title>
<link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
${body}
</body>
</html>`;
}
function buildNavXhtml(s, headings, chapterCount) {
    // Wir mappen Headings auf Kapitel-Files via Index.
    // Annahme: Headings sind in Dokumentreihenfolge; jedes H1 startet ein neues Kapitel.
    let chIndex = 0;
    const listItems = [];
    const filtered = headings.filter((h) => h.level <= s.toc.depth);
    for (const h of filtered) {
        if (h.level === 1)
            chIndex++;
        const href = chIndex > 0 ? `ch${chIndex}.xhtml#${h.id}` : `title.xhtml`;
        listItems.push(`<li class="nav-l${h.level}"><a href="${escapeXml(href)}">${escapeXml(h.text)}</a></li>`);
    }
    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" xml:lang="${s.meta.language}" lang="${s.meta.language}">
<head>
<meta charset="utf-8"/>
<title>${escapeXml(s.toc.title)}</title>
<link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
<nav epub:type="toc" id="toc">
  <h1>${escapeXml(s.toc.title)}</h1>
  <ol>
    ${listItems.join("\n    ")}
  </ol>
</nav>
</body>
</html>`;
}
function buildEpubCss(s) {
    // EPUB unterstützt @page nicht zuverlässig, also nur reine Typo.
    return `
body { font-family: ${quote(s.typography.bodyFontFamily)}, serif; font-size: 1em; line-height: ${s.typography.bodyLineHeight}; color: ${s.colors.text}; }
p { margin: 0 0 0.5em; text-align: ${s.typography.bodyAlignment}; text-indent: ${s.typography.firstLineIndentEm}em; hyphens: ${s.typography.hyphenation ? "auto" : "none"}; }
h1, h2, h3, h4 { font-family: ${quote(s.headings.h1.fontFamily)}, serif; page-break-after: avoid; break-after: avoid; }
h1 { font-size: 2em; page-break-before: always; break-before: page; margin: 2em 0 1em; }
h2 { font-size: 1.4em; margin: 1.6em 0 0.4em; }
h3 { font-size: 1.1em; margin: 1.4em 0 0.3em; }
h4 { font-size: 1em; font-style: italic; }
blockquote { font-style: italic; margin: 1em 2em; }
.pull-quote { font-size: 1.2em; text-align: center; font-style: italic; margin: 1.5em 2em; border-top: 1px solid ${s.colors.muted}; border-bottom: 1px solid ${s.colors.muted}; padding: 0.4em 0; }
img { max-width: 100%; height: auto; display: block; margin: 1em auto; }
figcaption { font-size: 0.85em; font-style: italic; text-align: center; color: ${s.colors.muted}; }
hr { border: 0; text-align: center; margin: 1.5em 0; }
hr::after { content: "* * *"; color: ${s.colors.muted}; letter-spacing: 0.5em; }
.title-page { text-align: center; margin-top: 30%; }
.title-page .book-title { font-size: 2.4em; font-weight: 700; }
.title-page .book-subtitle { font-style: italic; color: ${s.colors.muted}; }
.title-page .book-author { margin-top: 3em; }
nav#toc ol { list-style: none; padding-left: 0; }
nav#toc .nav-l2 { padding-left: 1em; }
nav#toc .nav-l3 { padding-left: 2em; font-size: 0.92em; }
nav#toc .nav-l4 { padding-left: 3em; font-size: 0.88em; }
`;
}
function quote(s) {
    if (/^[A-Za-z0-9_-]+$/.test(s))
        return s;
    return `"${s}"`;
}
function escapeXml(s) {
    return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" }[c]));
}
function randomUUID() {
    if (typeof crypto !== "undefined" && crypto.randomUUID)
        return crypto.randomUUID();
    // Fallback v4
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}
export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
    }, 0);
}
